/**
 * Snap utilities for canvas editors
 * Provides snapping to center lines and other elements/shapes
 *
 * Unified module used by:
 * - Template Editor (svg-editor/components/KonvaCanvas.tsx) - uses mm units
 * - Carousel Editor (carousel-editor/elements/*.tsx) - uses pixel units
 */

export interface SnapGuide {
    type: 'horizontal' | 'vertical';
    position: number;
}

export interface SnapResult {
    x: number;
    y: number;
    guides: SnapGuide[];
}

/**
 * Minimum interface for snappable elements/shapes
 * Works with TemplateShape, CarouselSlide elements, or any object with these properties
 */
export interface SnapableElement {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
}

interface ElementBounds {
    left: number;
    right: number;
    top: number;
    bottom: number;
    centerX: number;
    centerY: number;
}

/**
 * Calculate bounds for an element
 */
function getElementBounds(x: number, y: number, width: number, height: number): ElementBounds {
    return {
        left: x,
        right: x + width,
        top: y,
        bottom: y + height,
        centerX: x + width / 2,
        centerY: y + height / 2,
    };
}

/**
 * Calculate snap positions for an element being dragged
 * Snaps to workspace center and edges/centers of other elements
 *
 * @param draggedElement - The element being dragged (id is optional)
 * @param otherElements - Other elements to snap to (must have id, x, y, width, height)
 * @param workAreaWidth - Width of the work area
 * @param workAreaHeight - Height of the work area
 * @param threshold - Snap threshold (5 for mm in template editor, 10 for px in carousel)
 * @returns SnapResult with adjusted x, y and any active snap guides
 */
export function calculateSnap(
    draggedElement: { x: number; y: number; width: number; height: number; id?: string },
    otherElements: SnapableElement[],
    workAreaWidth: number,
    workAreaHeight: number,
    threshold: number = 10
): SnapResult {
    const guides: SnapGuide[] = [];
    let snapX = draggedElement.x;
    let snapY = draggedElement.y;

    const dragBounds = getElementBounds(
        draggedElement.x,
        draggedElement.y,
        draggedElement.width,
        draggedElement.height
    );

    // Collect all snap targets
    const verticalTargets: number[] = [];
    const horizontalTargets: number[] = [];

    // Workspace center lines
    verticalTargets.push(workAreaWidth / 2);
    horizontalTargets.push(workAreaHeight / 2);

    // Workspace edges
    verticalTargets.push(0, workAreaWidth);
    horizontalTargets.push(0, workAreaHeight);

    // Other elements' edges and centers
    otherElements.forEach(element => {
        // Skip the dragged element itself
        if (draggedElement.id && element.id === draggedElement.id) return;

        const bounds = getElementBounds(element.x, element.y, element.width, element.height);

        // Vertical targets (x positions)
        verticalTargets.push(bounds.left, bounds.right, bounds.centerX);

        // Horizontal targets (y positions)
        horizontalTargets.push(bounds.top, bounds.bottom, bounds.centerY);
    });

    // Check vertical snapping (affects X position)
    const dragVerticalPoints = [dragBounds.left, dragBounds.right, dragBounds.centerX];
    let bestVerticalSnap: { offset: number; target: number } | null = null;

    for (const target of verticalTargets) {
        for (const point of dragVerticalPoints) {
            const distance = Math.abs(point - target);

            if (distance <= threshold) {
                if (!bestVerticalSnap || distance < Math.abs(bestVerticalSnap.offset)) {
                    const offset = target - point;
                    bestVerticalSnap = { offset, target };
                }
            }
        }
    }

    if (bestVerticalSnap) {
        snapX = draggedElement.x + bestVerticalSnap.offset;
        guides.push({ type: 'vertical', position: bestVerticalSnap.target });
    }

    // Check horizontal snapping (affects Y position)
    const dragHorizontalPoints = [dragBounds.top, dragBounds.bottom, dragBounds.centerY];
    let bestHorizontalSnap: { offset: number; target: number } | null = null;

    for (const target of horizontalTargets) {
        for (const point of dragHorizontalPoints) {
            const distance = Math.abs(point - target);

            if (distance <= threshold) {
                if (!bestHorizontalSnap || distance < Math.abs(bestHorizontalSnap.offset)) {
                    const offset = target - point;
                    bestHorizontalSnap = { offset, target };
                }
            }
        }
    }

    if (bestHorizontalSnap) {
        snapY = draggedElement.y + bestHorizontalSnap.offset;
        guides.push({ type: 'horizontal', position: bestHorizontalSnap.target });
    }

    return { x: snapX, y: snapY, guides };
}
